import React, { useState } from 'react';
import { api, xeroConnectUrl } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { Wordmark } from '../components/ui/Wordmark';
import { Button } from '../components/ui/Button';
import { FieldInput } from '../components/ui/FieldInput';
import { Arrow, Check, XeroX } from '../components/ui/Icons';

type Provider = 'xero' | 'myob' | 'quickbooks';

export function Signup() {
  const [picked, setPicked] = useState<Provider | null>(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  function startXero() {
    setPicked('xero');
    window.location.href = xeroConnectUrl();
  }

  async function joinWaitlist(provider: 'myob' | 'quickbooks') {
    setError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${(import.meta.env.VITE_API_BASE as string | undefined) ?? ''}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, provider }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setJoined(provider);
    } catch (err) {
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  const providers: Array<{
    id: Provider;
    name: string;
    tagline: string;
    status: 'live' | 'soon';
    icon: React.ReactNode;
  }> = [
    {
      id: 'xero',
      name: 'Xero',
      tagline: 'Sign up instantly with your Xero account',
      status: 'live',
      icon: <XeroX size={40} />,
    },
    {
      id: 'myob',
      name: 'MYOB',
      tagline: 'AccountRight and MYOB Business',
      status: 'soon',
      icon: (
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#6B2C91', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18 }}>
          M
        </div>
      ),
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      tagline: 'QuickBooks Online',
      status: 'soon',
      icon: (
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#2CA01C', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18 }}>
          Q
        </div>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.navy, color: T.cream, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{ padding: '20px 24px', borderBottom: `1px solid ${T.navyLine}` }}>
        <a href="#/" style={{ textDecoration: 'none', display: 'inline-block' }}>
          <Wordmark size={22} />
        </a>
      </header>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <h1 style={{ ...sx.serif, fontSize: 'clamp(28px, 5vw, 44px)', lineHeight: 1.05, letterSpacing: '-0.02em', margin: '0 0 14px' }}>
            Which platform do you use?
          </h1>
          <p style={{ fontSize: 15, color: T.creamMuted, lineHeight: 1.55, margin: '0 0 32px' }}>
            Pick your accounting platform. We'll take you straight into a connected workspace. Your first 20 receipts are on us — no card required.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {providers.map((p) => {
              const isLive = p.status === 'live';
              const isJoined = joined === p.id;
              const showForm = picked === p.id && !isLive && !isJoined;

              return (
                <div
                  key={p.id}
                  style={{
                    background: T.navy2,
                    border: `1px solid ${T.navyLine}`,
                    borderRadius: 14,
                    padding: showForm ? '20px 20px 22px' : '18px 20px',
                    transition: 'padding 150ms ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {p.icon}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 17, fontWeight: 500 }}>{p.name}</span>
                        {!isLive && (
                          <span style={{ padding: '2px 8px', borderRadius: 100, fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(245,198,109,0.15)', color: '#F5C66D', border: '1px solid rgba(245,198,109,0.35)' }}>
                            Coming soon
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: T.creamDim, marginTop: 2 }}>{p.tagline}</div>
                    </div>
                    {isLive ? (
                      <Button size="small" onClick={startXero}>
                        Continue <Arrow color={T.navy} size={14} />
                      </Button>
                    ) : isJoined ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 100, background: 'rgba(168,199,158,0.18)', color: T.sage, fontSize: 12, fontWeight: 500 }}>
                        <Check size={12} color={T.sage} /> You're on the list
                      </div>
                    ) : (
                      <Button variant="ghostDark" size="small" onClick={() => setPicked(p.id)} disabled={submitting}>
                        Notify me
                      </Button>
                    )}
                  </div>

                  {showForm && (
                    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <p style={{ fontSize: 13, color: T.creamMuted, margin: 0, lineHeight: 1.5 }}>
                        {p.name} integration is on the way. Drop your email and we'll let you know the moment it's live.
                      </p>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <FieldInput
                          label=""
                          value={email}
                          onChange={setEmail}
                          placeholder="you@business.com"
                          type="email"
                          style={{ flex: 1 }}
                        />
                        <Button
                          size="small"
                          onClick={() => joinWaitlist(p.id as 'myob' | 'quickbooks')}
                          disabled={submitting}
                          style={{ alignSelf: 'flex-end', marginBottom: 1 }}
                        >
                          {submitting ? 'Saving…' : 'Join waitlist'}
                        </Button>
                      </div>
                      {error && <span style={{ fontSize: 12, color: T.fail }}>{error}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p style={{ fontSize: 12, color: T.creamDim, textAlign: 'center', marginTop: 28, lineHeight: 1.5 }}>
            Already have an account? <a href="#/login" style={{ color: T.sage, textDecoration: 'none' }}>Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
}
